import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { TrendingUp, TrendingDown, Wallet, Users, Tags, CreditCard, BarChart3, CalendarRange, RotateCcw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const formatCurrency = (val) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val || 0);
const CHART_MARGIN = { top: 20, right: 30, left: 20, bottom: 5 };
const BAR_RADIUS = [2, 2, 0, 0];
const TOOLTIP_STYLE = { borderRadius: "4px", border: "1px solid #E5E5E3", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" };
const LEGEND_STYLE = { fontSize: "12px" };
const BANK_METHOD_KEYWORDS = ["BANKA", "HAVALE", "EFT", "IBAN", "İBAN", "TRANSFER", "VİRMAN", "VIRMAN"];

const getMonthValue = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  return `${year}-${month}`;
};

const getMonthLabel = (monthValue) => {
  const [year, month] = monthValue.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("tr-TR", {
    month: "long",
    year: "numeric",
  });
};

const buildDateParams = (selectedMonth, startDate, endDate) => {
  if (startDate || endDate) {
    return {
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    };
  }

  if (selectedMonth !== "all") {
    const [year, month] = selectedMonth.split("-").map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    const startDateValue = `${start.getFullYear()}-${`${start.getMonth() + 1}`.padStart(2, "0")}-${`${start.getDate()}`.padStart(2, "0")}`;
    const endDateValue = `${end.getFullYear()}-${`${end.getMonth() + 1}`.padStart(2, "0")}-${`${end.getDate()}`.padStart(2, "0")}`;
    return { start_date: startDateValue, end_date: endDateValue };
  }

  return {};
};

const normalizePaymentMethodType = (rawType, rawName = "") => {
  if (rawType === "nakit") return "nakit";
  if (rawType === "kredi_karti") return "kredi_karti";
  if (rawType === "banka") return "banka";
  if (rawName.toUpperCase().includes("KART")) return "kredi_karti";
  if (BANK_METHOD_KEYWORDS.some((keyword) => rawName.toUpperCase().includes(keyword))) return "banka";
  return "nakit";
};

function DashboardFilters({
  selectedMonth,
  setSelectedMonth,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  monthOptions,
}) {
  const isRangeActive = Boolean(startDate || endDate);

  return (
    <Card className="swiss-card">
      <CardHeader className="border-b border-border bg-neutral-50/60 pb-4">
        <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
          <CalendarRange className="w-4 h-4" /> Özet Filtreleri
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Ay Seçimi</label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="h-10 bg-white" data-testid="dashboard-month-select">
                <SelectValue placeholder="Ay seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Aylar</SelectItem>
                {monthOptions.map((month) => (
                  <SelectItem key={month} value={month}>{getMonthLabel(month)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Başlangıç Tarihi</label>
            <Input
              type="date"
              className="h-10 bg-white"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="dashboard-start-date-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Bitiş Tarihi</label>
            <Input
              type="date"
              className="h-10 bg-white"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="dashboard-end-date-input"
            />
          </div>

          <div className="space-y-2 flex flex-col justify-end">
            <Button
              type="button"
              variant="outline"
              className="h-10"
              onClick={() => {
                setSelectedMonth("all");
                setStartDate("");
                setEndDate("");
              }}
              data-testid="dashboard-clear-filters-button"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Filtreleri Temizle
            </Button>
          </div>
        </div>

        <div className="text-xs text-muted-foreground" data-testid="dashboard-active-filter-note">
          {isRangeActive
            ? "Tarih aralığı aktif. Ay filtresi bu durumda devre dışıdır."
            : selectedMonth !== "all"
              ? `${getMonthLabel(selectedMonth)} dönemi gösteriliyor.`
              : "Tüm zamanlar gösteriliyor."}
        </div>
      </CardContent>
    </Card>
  );
}

function AccountReportCard({ accounts, transactions, reportAccountId, setReportAccountId }) {
  const accountReport = useMemo(() => {
    if (reportAccountId === "none") return null;

    let income = 0;
    let expense = 0;
    let debtPayment = 0;
    let count = 0;

    transactions.forEach((tx) => {
      if (tx.currency === "TL" && tx.account_id === reportAccountId) {
        if (tx.type === "income") income += tx.amount;
        else if (tx.type === "expense") expense += tx.amount;
        else if (tx.type === "debt_payment") debtPayment += tx.amount;
        count += 1;
      }
    });

    const accName = accounts.find((account) => account.id === reportAccountId)?.name || "Bilinmiyor";
    return { name: accName, income, expense, debtPayment, balance: income - expense - debtPayment, count };
  }, [reportAccountId, transactions, accounts]);

  return (
    <Card className="swiss-card border-l-4 border-l-primary">
      <CardHeader className="pb-4 border-b border-border bg-neutral-50/50">
        <div className="flex flex-col space-y-3">
          <CardTitle className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center">
            <Users className="w-4 h-4 mr-2" /> Cari Hesap Özeti
          </CardTitle>
          <Select value={reportAccountId} onValueChange={setReportAccountId}>
            <SelectTrigger className="h-10 bg-white" data-testid="report-account-select">
              <SelectValue placeholder="Raporlanacak Cari Hesap Seçin..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seçim Yapılmadı --</SelectItem>
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {accountReport ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Toplam İşlem:</span>
              <span className="font-bold">{accountReport.count} adet</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Hesaptan Gelen:</span>
              <span className="font-bold text-income">{formatCurrency(accountReport.income)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Hesaba Çıkan:</span>
              <span className="font-bold text-expense">{formatCurrency(accountReport.expense)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Borç Ödeme:</span>
              <span className="font-bold text-blue-600">{formatCurrency(accountReport.debtPayment)}</span>
            </div>
            <div className="pt-3 mt-3 border-t border-dashed flex justify-between items-center">
              <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Net Durum:</span>
              <span className={`text-xl font-heading font-bold ${accountReport.balance >= 0 ? "text-income" : "text-expense"}`}>
                {accountReport.balance > 0 ? "+" : ""}{formatCurrency(accountReport.balance)}
              </span>
            </div>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
            Raporu görmek için yukarıdan bir cari hesap seçin.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TagReportCard({ tags, transactions, reportTagId, setReportTagId }) {
  const tagReport = useMemo(() => {
    if (reportTagId === "none") return null;

    let income = 0;
    let expense = 0;
    let debtPayment = 0;
    let count = 0;

    transactions.forEach((tx) => {
      if (tx.currency === "TL" && tx.tags.includes(reportTagId)) {
        if (tx.type === "income") income += tx.amount;
        else if (tx.type === "expense") expense += tx.amount;
        else if (tx.type === "debt_payment") debtPayment += tx.amount;
        count += 1;
      }
    });

    const tagName = tags.find((tag) => tag.id === reportTagId)?.name || "Bilinmiyor";
    return { name: tagName, income, expense, debtPayment, balance: income - expense - debtPayment, count };
  }, [reportTagId, transactions, tags]);

  return (
    <Card className="swiss-card border-l-4 border-l-primary">
      <CardHeader className="pb-4 border-b border-border bg-neutral-50/50">
        <div className="flex flex-col space-y-3">
          <CardTitle className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center">
            <Tags className="w-4 h-4 mr-2" /> Etiket Özeti
          </CardTitle>
          <Select value={reportTagId} onValueChange={setReportTagId}>
            <SelectTrigger className="h-10 bg-white" data-testid="report-tag-select">
              <SelectValue placeholder="Raporlanacak Etiket Seçin..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seçim Yapılmadı --</SelectItem>
              {tags.map((tag) => (
                <SelectItem key={tag.id} value={tag.id}>{tag.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {tagReport ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Toplam İşlem:</span>
              <span className="font-bold">{tagReport.count} adet</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Etiketle Gelen:</span>
              <span className="font-bold text-income">{formatCurrency(tagReport.income)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Etiketle Gider:</span>
              <span className="font-bold text-expense">{formatCurrency(tagReport.expense)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Borç Ödeme:</span>
              <span className="font-bold text-blue-600">{formatCurrency(tagReport.debtPayment)}</span>
            </div>
            <div className="pt-3 mt-3 border-t border-dashed flex justify-between items-center">
              <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Net Etki:</span>
              <span className={`text-xl font-heading font-bold ${tagReport.balance >= 0 ? "text-income" : "text-expense"}`}>
                {tagReport.balance > 0 ? "+" : ""}{formatCurrency(tagReport.balance)}
              </span>
            </div>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
            Raporu görmek için yukarıdan bir etiket seçin.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreditCardSummary({ transactions, paymentMethods }) {
  const ccMethods = useMemo(() => paymentMethods.filter((method) => normalizePaymentMethodType(method.type, method.name) === "kredi_karti"), [paymentMethods]);

  const ccReport = useMemo(() => {
    const report = [];
    let grandTotal = 0;

    ccMethods.forEach((card) => {
      const total = transactions
        .filter((tx) => tx.currency === "TL" && tx.type === "expense" && tx.payment_method_id === card.id)
        .reduce((accumulator, tx) => accumulator + tx.amount, 0);

      if (total > 0) {
        report.push({ name: card.name, total });
        grandTotal += total;
      }
    });

    const unmappedTotal = transactions
      .filter((tx) => tx.currency === "TL" && tx.type === "expense" && normalizePaymentMethodType(tx.payment_method_type, tx.payment_method_name) === "kredi_karti" && !ccMethods.find((card) => card.id === tx.payment_method_id))
      .reduce((accumulator, tx) => accumulator + tx.amount, 0);

    if (unmappedTotal > 0) {
      report.push({ name: "Silinmiş / Diğer Kredi Kartları", total: unmappedTotal });
      grandTotal += unmappedTotal;
    }

    return { list: report.sort((a, b) => b.total - a.total), grandTotal };
  }, [transactions, ccMethods]);

  if (ccReport.list.length === 0) return null;

  return (
    <Card className="swiss-card border-l-4 border-l-blue-600" data-testid="credit-card-summary-section">
      <CardHeader className="pb-4 border-b border-border bg-neutral-50/50">
        <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center">
          <CreditCard className="w-4 h-4 mr-2" /> Kredi Kartı Harcamaları (TL)
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {ccReport.list.map((card, index) => (
          <div key={`${card.name}-${index}`} className="flex justify-between items-center text-sm border-b border-border/50 pb-2 last:border-0 last:pb-0">
            <span className="font-medium text-foreground">{card.name}</span>
            <span className="font-bold text-expense">{formatCurrency(card.total)}</span>
          </div>
        ))}
        <div className="pt-3 mt-2 border-t border-dashed flex justify-between items-center">
          <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Genel Kredi Kartı Gideri:</span>
          <span className="text-xl font-heading font-bold text-blue-600" data-testid="credit-card-grand-total-value">
            {formatCurrency(ccReport.grandTotal)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardStats({ tlSummary }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Kasa (Nakit)</CardTitle>
          <Wallet className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading" data-testid="cash-balance-value">{formatCurrency(tlSummary.balance)}</div>
          <p className="mt-2 text-xs text-muted-foreground">Kasaya giren nakit eksi nakit gider ve nakit borç ödemedir.</p>
        </CardContent>
      </Card>

      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Toplam Gelir</CardTitle>
          <TrendingUp className="h-4 w-4 text-income" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading text-income" data-testid="cash-income-value">{formatCurrency(tlSummary.total_income ?? tlSummary.income)}</div>
          <p className="mt-2 text-xs text-muted-foreground">İşlemler &gt; Gelir filtresindeki toplamla aynı mantıkta gösterilir.</p>
        </CardContent>
      </Card>

      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Nakit Gider</CardTitle>
          <TrendingDown className="h-4 w-4 text-expense" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading text-expense" data-testid="cash-expense-value">{formatCurrency(tlSummary.expense)}</div>
        </CardContent>
      </Card>

      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Nakit Borç Ödeme</CardTitle>
          <TrendingDown className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading text-blue-600" data-testid="cash-debt-value">{formatCurrency(tlSummary.debt_payment)}</div>
        </CardContent>
      </Card>

      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Toplam Giderler</CardTitle>
          <BarChart3 className="h-4 w-4 text-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading text-foreground" data-testid="overall-expense-value">{formatCurrency(tlSummary.total_expense)}</div>
          <p className="mt-2 text-xs text-muted-foreground">Nakit, banka ve kredi kartı ile yapılan tüm giderlerin toplamı.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function MonthlyCashFlowChart({ transactions }) {
  const monthlyData = useMemo(() => {
    const monthlyMap = {};

    transactions
      .filter((tx) => tx.currency === "TL" && normalizePaymentMethodType(tx.payment_method_type, tx.payment_method_name) === "nakit")
      .forEach((tx) => {
        const date = new Date(tx.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthLabel = date.toLocaleString("tr-TR", { month: "short", year: "numeric" });

        if (!monthlyMap[monthKey]) {
          monthlyMap[monthKey] = { month: monthLabel, income: 0, expense: 0, debt_payment: 0, sortKey: date.getTime() };
        }

        if (tx.type === "income") monthlyMap[monthKey].income += tx.amount;
        else if (tx.type === "expense") monthlyMap[monthKey].expense += tx.amount;
        else if (tx.type === "debt_payment") monthlyMap[monthKey].debt_payment += tx.amount;
      });

    return Object.values(monthlyMap).sort((a, b) => a.sortKey - b.sortKey).slice(-6);
  }, [transactions]);

  return (
    <Card className="swiss-card">
      <CardHeader>
        <CardTitle className="text-lg font-heading font-medium">Aylık Nakit Akışı (TL)</CardTitle>
      </CardHeader>
      <CardContent className="h-[300px] w-full">
        {monthlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E3" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#5C5C5C" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#5C5C5C" }} tickFormatter={(value) => `₺${value}`} />
              <Tooltip cursor={{ fill: "#F7F7F5" }} contentStyle={TOOLTIP_STYLE} />
              <Legend iconType="circle" wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="income" name="Kasaya Giren Nakit" fill="#276339" radius={BAR_RADIUS} />
              <Bar dataKey="expense" name="Nakit Gider" fill="#A13426" radius={BAR_RADIUS} />
              <Bar dataKey="debt_payment" name="Nakit Borç Ödeme" fill="#2563eb" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Seçilen filtre için yeterli nakit verisi yok.</div>
        )}
      </CardContent>
    </Card>
  );
}

function TagExpensePieChart({ transactions, tags }) {
  const tagData = useMemo(() => {
    const tagMap = {};
    const tlExpenses = transactions.filter((tx) => tx.currency === "TL" && tx.type === "expense");

    tlExpenses.forEach((tx) => {
      if (!tx.tags || tx.tags.length === 0) {
        if (!tagMap.Diğer) tagMap.Diğer = { name: "Diğer", value: 0, color: "#8F8F8F" };
        tagMap.Diğer.value += tx.amount;
        return;
      }

      tx.tags.forEach((tagId) => {
        const tag = tags.find((tagItem) => tagItem.id === tagId);
        const tagName = tag ? tag.name : "Bilinmeyen";
        const tagColor = tag ? tag.color : "#8F8F8F";

        if (!tagMap[tagName]) tagMap[tagName] = { name: tagName, value: 0, color: tagColor };
        tagMap[tagName].value += tx.amount;
      });
    });

    return Object.values(tagMap).sort((a, b) => b.value - a.value);
  }, [transactions, tags]);

  return (
    <Card className="swiss-card">
      <CardHeader>
        <CardTitle className="text-lg font-heading font-medium">Genel Gider Etiketleri (TL)</CardTitle>
      </CardHeader>
      <CardContent className="h-[300px] w-full">
        {tagData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={tagData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                {tagData.map((entry, index) => <Cell key={`cell-${entry.name}-${index}`} fill={entry.color} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => `₺${value}`} />
              <Legend iconType="circle" layout="vertical" verticalAlign="middle" align="right" wrapperStyle={LEGEND_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Seçilen filtre için gider verisi yok.</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [summary, setSummary] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [tags, setTags] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [monthSourceTransactions, setMonthSourceTransactions] = useState([]);
  const [reportAccountId, setReportAccountId] = useState("none");
  const [reportTagId, setReportTagId] = useState("none");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const dateParams = useMemo(() => buildDateParams(selectedMonth, startDate, endDate), [selectedMonth, startDate, endDate]);

  const fetchMonthSource = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/transactions`, { withCredentials: true });
      setMonthSourceTransactions(data);
    } catch (error) {
      setMonthSourceTransactions([]);
    }
  }, [BACKEND_URL]);

  const fetchDashboard = useCallback(async () => {
    try {
      const requestConfig = { withCredentials: true, params: dateParams };
      const [dashRes, txRes, tagsRes, accRes, pmRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard`, requestConfig),
        axios.get(`${BACKEND_URL}/api/transactions`, requestConfig),
        axios.get(`${BACKEND_URL}/api/tags`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/payment_methods`, { withCredentials: true }),
      ]);

      setSummary(dashRes.data);
      setTransactions(txRes.data);
      setTags(tagsRes.data);
      setAccounts(accRes.data);
      setPaymentMethods(pmRes.data);
    } catch (error) {
      toast.error("Dashboard verileri yüklenemedi.");
    }
  }, [BACKEND_URL, dateParams]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    fetchMonthSource();
  }, [fetchMonthSource]);

  const monthOptions = useMemo(() => {
    const months = new Set(monthSourceTransactions.map((tx) => getMonthValue(tx.date)));
    return Array.from(months).sort((a, b) => b.localeCompare(a));
  }, [monthSourceTransactions]);

  const tlSummary = useMemo(() => ({
    income: 0,
    total_income: 0,
    expense: 0,
    debt_payment: 0,
    balance: 0,
    total_expense: 0,
    ...(summary.TL || {}),
  }), [summary]);

  const otherCurrencies = useMemo(() => {
    return Object.keys(summary)
      .filter((currency) => currency !== "TL")
      .map((currency) => ({ name: currency, balance: summary[currency].balance || 0 }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [summary]);

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between border-b border-border pb-4 gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Özet (TL)</h1>
          <p className="text-sm text-muted-foreground mt-2">Nakit kasa ve toplam gider yapısını ay veya tarih aralığına göre izleyin.</p>
        </div>
      </div>

      <DashboardFilters
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        monthOptions={monthOptions}
      />

      <DashboardStats tlSummary={tlSummary} />
      <CreditCardSummary transactions={transactions} paymentMethods={paymentMethods} />

      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-heading font-bold tracking-tight border-b border-border pb-2">Gelişmiş Raporlar (TL)</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AccountReportCard accounts={accounts} transactions={transactions} reportAccountId={reportAccountId} setReportAccountId={setReportAccountId} />
          <TagReportCard tags={tags} transactions={transactions} reportTagId={reportTagId} setReportTagId={setReportTagId} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <MonthlyCashFlowChart transactions={transactions} />
        <TagExpensePieChart transactions={transactions} tags={tags} />
      </div>

      {otherCurrencies.length > 0 && (
        <div className="mt-8 space-y-4">
          <h2 className="text-xl font-heading font-medium border-b border-border pb-2">Diğer Para Birimleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {otherCurrencies.map((currency) => (
              <Card key={currency.name} className="swiss-card border-l-4 border-l-primary">
                <CardHeader className="pb-2 bg-neutral-50/50 border-b border-border">
                  <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">{currency.name} Nakit Bakiyesi</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold font-heading">{currency.balance.toLocaleString()} {currency.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}